import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncThunkConfig } from '../../async-thunk-config';
import apiManager from '../../../api/api-manager';
import {
  CreateProspectPayload,
  FinalizeOnboardingState,
} from './finalize-onboarding-types';
import {
  CreateProspectResponse,
  FinalizeOnboardingResponse,
  StatusResponse,
  UpdateProspectRequest,
  UpdateProspectResponse,
  UpdateSessionRequest,
} from '../../../api/generated/dob';

export const getFinalizeOnboardingThunk = createAsyncThunk<
  FinalizeOnboardingResponse,
  string,
  AsyncThunkConfig<string>
>('get-finalize-onboarding', async (referralCode: string, thunkApi) => {
  try {
    const response = await apiManager.dobCustomerApi.getFinalizeOnboarding(
      referralCode
    );
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

const initialState: FinalizeOnboardingState = {
  getFinalizeOnboardingStatus: 'idle',
  deletePraticaStatus: 'idle',
  getFinalizeOnboardingResponse: undefined,
  referralCode: undefined,
  createProspectRequest: {
    error: undefined,
    status: 'idle',
    code: '',
    name: '',
  },
  updateProspectRequest: {
    error: undefined,
    status: 'idle',
    code: '',
    name: '',
  },
};

export const deletePraticaThunk = createAsyncThunk<
  StatusResponse,
  string,
  AsyncThunkConfig<string>
>('delete-pratica', async (referralCode: string, thunkApi) => {
  try {
    const response =
      await apiManager.dobCustomerApi.deletePraticaByReferralCode(referralCode);
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const postCreateProspect = createAsyncThunk<
  CreateProspectResponse,
  CreateProspectPayload,
  AsyncThunkConfig<string>
>('post-create-prospect', async (payload: CreateProspectPayload, thunkApi) => {
  try {
    const response = await apiManager.dobCustomerApi.createProspect(payload);
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const updateProspectSession = createAsyncThunk<
  StatusResponse,
  UpdateSessionRequest,
  AsyncThunkConfig<string>
>('post-create-prospect', async (request: UpdateSessionRequest, thunkApi) => {
  try {
    const response = await apiManager.dobCustomerApi.postUpdateSession(request);
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const postUpdateProspectOnboarding = createAsyncThunk<
  UpdateProspectResponse,
  UpdateProspectRequest,
  AsyncThunkConfig<string>
  >(
  'customerServiceCredentialRecovery',
  async (params: UpdateProspectRequest, thunkApi) => {
    try {
      const response = await apiManager.dobCustomerApi.updateProspect(params);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

const finalizeOnboardingSlice = createSlice({
  name: 'features/finalizeOnboarding',
  initialState,
  reducers: {
    setReferral(state, action: PayloadAction<string>) {
      state.referralCode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFinalizeOnboardingThunk.pending, (state) => {
      state.getFinalizeOnboardingStatus = 'loading';
      state.getFinalizeOnboardingResponse = undefined;
    });
    builder.addCase(getFinalizeOnboardingThunk.fulfilled, (state, action) => {
      state.getFinalizeOnboardingStatus = 'succeeded';
      state.getFinalizeOnboardingResponse = action.payload;
    });
    builder.addCase(getFinalizeOnboardingThunk.rejected, (state) => {
      state.getFinalizeOnboardingResponse = undefined;
      state.getFinalizeOnboardingStatus = 'failed';
    });
    builder.addCase(deletePraticaThunk.pending, (state) => {
      state.deletePraticaStatus = 'loading';
    });
    builder.addCase(deletePraticaThunk.fulfilled, (state) => {
      state.deletePraticaStatus = 'succeeded';
    });
    builder.addCase(deletePraticaThunk.rejected, (state) => {
      state.deletePraticaStatus = 'failed';
    });
    builder.addCase(postCreateProspect.pending, (state) => {
      state.createProspectRequest.status = 'loading';
      state.createProspectRequest.code = '';
      state.createProspectRequest.name = '';
      state.createProspectRequest.error = undefined;
    });
    builder.addCase(postCreateProspect.fulfilled, (state, action) => {
      state.createProspectRequest.status = 'succeeded';
      if (action.payload.status) {
        state.createProspectRequest.code = action.payload.status.code;
        state.createProspectRequest.name = action.payload.name;
      } else {
        state.createProspectRequest.code = 'ERROR';
        state.createProspectRequest.name = '';
      }
      state.createProspectRequest.error = undefined;
    });
    builder.addCase(postCreateProspect.rejected, (state) => {
      state.createProspectRequest.status = 'failed';
      state.createProspectRequest.code = '';
      state.createProspectRequest.name = '';
      state.createProspectRequest.error = '';
    });
    builder.addCase(postUpdateProspectOnboarding.pending, (state) => {
      state.updateProspectRequest.status = 'loading';
      state.updateProspectRequest.code = '';
      state.updateProspectRequest.name = '';
      state.updateProspectRequest.error = undefined;
    });
    builder.addCase(postUpdateProspectOnboarding.fulfilled, (state, action) => {
      state.updateProspectRequest.status = 'succeeded';
      if (action.payload .status) {
        state.updateProspectRequest.code = action.payload.status.code;
        state.updateProspectRequest.name = action.payload.name;
      } else {
        state.updateProspectRequest.code = 'ERROR';
        state.updateProspectRequest.name = '';
      }
      state.updateProspectRequest.error = undefined;
    });
    builder.addCase(postUpdateProspectOnboarding.rejected, (state) => {
      state.updateProspectRequest.status = 'failed';
      state.updateProspectRequest.code = '';
      state.updateProspectRequest.name = '';
      state.updateProspectRequest.error = '';
    });
  },
});

export const finalizeOnboardingSliceActions = finalizeOnboardingSlice.actions;

export default finalizeOnboardingSlice.reducer;
